import { useId } from "react";

import { twMerge } from "tailwind-merge";

import { ConnectWalletButton } from "features/connect-wallet";
import { ErrorIcon } from "shared/ui/icon";

import bgSrc from "../assets/bg.png";
import galacticaNetworkSrc from "../assets/galactica-network.svg";
import starSrc from "../assets/star.svg";
import { useAuthStatus } from "../use-auth-status";
import { Book } from "./book";

type Props = {
  className?: string;
  isMetamaskNeeded?: boolean;
  isSnapNeeded?: boolean;
  onComplete?(): void;
};

export const Auth = ({
  isMetamaskNeeded,
  isSnapNeeded,
  className,
  ...props
}: Props) => {
  const id = useId();
  const { isWrongChain } = useAuthStatus({
    isMetamaskNeeded,
    isSnapNeeded,
  });

  return (
    <Book
      isMetamaskNeeded={isMetamaskNeeded}
      isSnapNeeded={isSnapNeeded}
      onComplete={props.onComplete}
    >
      <div
        className="bg-whiteSmoke absolute top-0 left-0 size-full rounded-xl object-cover"
        style={{ backfaceVisibility: "hidden" }}
      >
        <div
          className={twMerge(
            "flex w-[480px] max-w-[480px] flex-col items-center rounded-xl bg-cover bg-no-repeat py-14",
            className
          )}
          style={{
            backgroundImage: `url("/passport/texture-wave.png")`,
          }}
        >
          <h2 className="font-ptm mb-8 text-[40px] leading-[130%] uppercase">
            Passport
          </h2>
          <div className="relative">
            <img
              className="absolute top-6 left-1/3 z-30 -translate-x-1/3"
              src={starSrc}
            />
            <img
              className={twMerge(
                "relative flex size-[291px]",
                isWrongChain && "grayscale"
              )}
              src={bgSrc}
              style={{
                clipPath: `url(#clipped-image-${id})`,
              }}
            />
            <svg
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              height="291px"
              viewBox="0 0 291 291"
              width="291px"
            >
              <defs>
                <clipPath
                  className="animate-spin-slow origin-center"
                  id={`clipped-image-${id}`}
                  style={{
                    animationDirection: "reverse",
                  }}
                >
                  <path
                    clipRule="evenodd"
                    d="M195.645 17.0702C194.986 16.7668 194.354 16.3534 193.664 16.1765C168.15 9.64684 142.948 10.6803 118.252 19.936C108.647 23.536 99.6058 28.3566 91.2002 34.4692C83.9247 39.7597 77.2628 45.678 71.4016 52.4402C51.6469 75.2315 42.2451 101.76 43.9859 132.176C45.1519 152.546 51.7862 170.968 64.4196 186.778C79.14 205.198 98.0828 216.651 121.735 218.957C137.314 220.476 152.182 217.823 165.828 209.701C171.698 206.206 177.001 201.934 181.707 196.917C183.15 195.378 184.674 193.869 186.373 192.649C187.104 192.124 188.633 191.947 189.327 192.383C190.01 192.812 190.583 194.36 190.353 195.168C189.59 197.847 188.805 200.641 187.377 202.982C183.128 209.943 177.662 215.804 170.88 220.369C144.896 237.857 117.499 238.098 89.5079 226.572C74.7244 220.485 62.4671 210.611 52.1277 198.162C34.3984 176.814 26.3914 151.943 25.8589 124.487C25.5511 108.608 28.9468 93.2063 34.6773 78.4292C39.3603 66.3534 45.5374 55.076 53.8612 45.136C57.5325 40.7519 61.4905 36.6136 65.3145 32.3598C66.0383 31.5547 66.7434 30.7327 67.4575 29.9181C67.3451 29.7805 67.2328 29.6424 67.1204 29.5043C66.1381 30.3164 65.0673 31.0411 64.1876 31.9541C56.1699 40.2754 48.6962 49.0068 42.6486 58.9815C32.0958 76.3858 25.6781 95.1298 23.9935 115.486C22.5653 132.739 24.2512 149.679 29.8547 166.077C34.6663 180.159 41.6035 193.146 51.4067 204.318C74.7875 230.963 103.907 244.243 139.354 241.729C157.604 240.434 174.265 234.169 188.506 222.433C210.329 204.451 221.26 181.264 219.691 152.476C219.56 150.077 219.314 147.661 219.479 145.278C219.549 144.254 220.384 142.651 221.137 142.475C221.99 142.276 223.499 143.121 224.099 143.966C226.79 147.755 227.628 152.286 228.264 156.763C230.801 174.609 227.292 191.294 218.597 206.961C210.295 221.92 198.857 233.826 184.299 242.498C157.964 258.185 129.815 261.207 100.434 253.84C82.0671 249.235 65.8714 240.256 51.5785 227.747C36.6571 214.687 25.3451 198.936 17.5371 180.606C15.7153 176.33 13.9981 172.007 12 167.755C12.8244 170.491 13.501 173.284 14.496 175.955C24.3807 202.486 40.5259 224.138 63.8212 240.054C84.3856 254.103 107.142 261.532 131.977 261.484C158.358 261.433 182.179 253.303 202.78 236.393C223.268 219.576 235.625 197.849 239.416 171.428C242.311 151.253 238.636 132.025 228.527 114.426C219.956 99.5039 207.793 88.2431 191.812 81.6211C188.115 80.0888 184.311 78.82 180.558 77.4233C179.54 77.0445 178.381 76.8384 177.559 76.1957C176.738 75.5535 175.644 74.3432 175.786 73.5995C175.963 72.6732 177.163 71.5388 178.134 71.2651C179.862 70.7778 181.757 70.6776 183.57 70.7502C194.813 71.1995 204.855 75.302 214.16 81.4327C234.328 94.7212 246.644 113.613 252.17 137.096C257.743 160.776 255.13 183.796 245.543 205.996C237.607 224.372 225.391 239.497 209.86 251.788C199.824 259.731 188.692 265.813 176.709 270.392C165.503 274.673 153.998 277.251 142.116 278.336C141.243 278.416 140.378 278.587 139.51 278.935C141.254 278.935 143.015 279.097 144.739 278.909C157.943 277.467 170.922 274.916 183.198 269.637C198.24 263.169 211.855 254.501 223.569 242.763C232.426 233.888 240.014 224.145 245.845 213.023C257.946 189.945 262.355 165.467 258.16 139.627C253.718 112.265 240.863 89.7605 218.818 73.132C201.49 60.0619 181.891 53.6588 160.134 54.928C146.363 55.7315 133.522 59.9391 122.085 67.6111C107.174 77.6146 97.0883 91.3892 92.9423 109.28C92.5107 111.144 92.025 113.053 91.164 114.731C90.6832 115.667 89.3845 116.703 88.4408 116.717C87.5065 116.731 85.9424 115.675 85.7217 114.81C84.9523 111.795 84.2504 108.661 84.2382 105.569C84.2048 97.1336 86.9602 89.345 90.7373 81.9776C100.432 63.0679 115.747 50.7439 135.346 43.5893C163.493 33.3141 190.527 37.0142 216.558 50.7081C231.143 58.3809 243.184 69.2311 253.157 82.509C265.798 99.3378 273.552 118.197 276.648 139.043C277.525 144.943 278.04 150.898 278.771 157.274C279.439 149.466 278.545 142.18 277.362 134.955C273.974 114.248 266.098 95.4006 253.489 78.74C243.006 64.8883 230.15 53.7965 214.762 45.6949C197.459 36.5851 179.091 31.983 159.69 32.7909C137.257 33.7251 116.549 40.7078 99.4117 55.635C73.703 78.0273 62.2049 106.293 69.1861 140.65C73.3142 160.964 84.7752 176.398 102.987 186.226C107.129 188.461 111.825 189.648 116.276 191.29C117.866 191.876 119.624 192.148 121.045 193.005C121.997 193.58 123.053 194.943 123.035 195.936C123.019 196.942 121.905 198.561 120.984 198.805C118.007 199.596 114.87 200.422 111.844 200.262C98.8498 199.579 87.6433 194.386 77.7468 185.901C62.8995 173.172 54.5639 156.806 50.9833 137.712C46.7722 115.255 50.8714 94.0213 61.1412 74.0439C70.214 56.3947 83.4513 42.4684 100.197 31.8688C121.089 18.6454 143.853 12.7332 168.318 13.2563C177.549 13.4537 186.688 14.8178 195.676 17.0587L195.645 17.0702Z"
                    fillRule="evenodd"
                  />
                </clipPath>
              </defs>
            </svg>
          </div>
          <p
            className={twMerge(
              "text-grapefruit mt-4 font-medium",
              !isWrongChain && "appearance-none opacity-0"
            )}
          >
            Wrong network selected
          </p>

          <ConnectWalletButton
            className={twMerge(
              "font-ptm border-basketBallOrange/75 mt-4 mb-8 h-[54px] w-[227px] border uppercase",
              isWrongChain && "border-grapefruit mt-4 w-auto"
            )}
            connectContent={
              <span className="tracking-tighter">Connect MetaMask</span>
            }
            switchChainContent={
              <span className="flex items-center gap-x-6">
                <ErrorIcon /> Switch to Galactica
              </span>
            }
            theme="basketBallOrange-transparent"
          />
          <img className="mt-auto" src={galacticaNetworkSrc} />
        </div>
      </div>
    </Book>
  );
};
